import React, { useEffect, useState } from "react";
import { XCrossIcon } from "../../../../assets";
import { useMessage } from "../../../../context/MessageContext";
import { isEqual } from "lodash";
import { getApi, postApi, putApi } from "../../../../apis/api";
import {
  GENERAL_ERROR_MESSAGE,
  SERVICE_CONFIG_MESSAGES,
} from "../../../../utils/message";

import { Loader } from "../../../../components/loaders/loaders";
import { AddEditOrderStatusModalProps } from "../OrderStatusTypes";
import { UPDATE_STATUS_ENDPOINT } from "../../../../apis/end-points";


const AddEditOrderStatusModal: React.FC<AddEditOrderStatusModalProps> = ({
  isEdit = false,
  isOpen,
  onClose,
  initialData = null,
  onSuccess,
}) => {
  const { setMessage } = useMessage();

  const [data, setData] = useState({
    onwardServiceName: "",
    description: "",
  });

  const [initialPayload, setInitialPayload] = useState<any>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize form
  useEffect(() => {
    if (!isOpen) return;

    if (!isEdit || !initialData) {
      const empty = { onwardServiceName: "", description: "" };
      setData(empty);
      setInitialPayload(empty);
      return;
    }

    const payload = {
      onwardServiceName: initialData.name || "",
      description:
        initialData.onwardStatusName === "--"
          ? ""
          : initialData.onwardStatusName || "",
    };


    setData(payload);
    setInitialPayload(payload);
  }, [isEdit, initialData, isOpen]);

  // Track changes (edit mode only)
  useEffect(() => {
    if (!isEdit || !initialPayload) return;
    setIsChanged(!isEqual(initialPayload, data));
  }, [data, initialPayload, isEdit]);

  const resetForm = () => {
    setData({ onwardServiceName: "", description: "" });
    setInitialPayload(null);
    setIsChanged(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    const payload = {
      onwardStatusName: data.description.trim(),
    };

    try {
      setLoading(true);
      let apiResponse;

      if (isEdit && initialData?.id) {
        apiResponse = await putApi(
          `${UPDATE_STATUS_ENDPOINT}${initialData.id}`,
          payload
        );
      }

      if (apiResponse?.success) {
        setMessage(
          isEdit
            ? SERVICE_CONFIG_MESSAGES.ONWARD_ORDER_STATUS_UPDATE_SUCCESS
            : SERVICE_CONFIG_MESSAGES.ONWARD_ORDER_STATUS_ADD_SUCCESS,
          "success"
        );
        onSuccess?.();
        handleClose();
      } else {
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error("Order status save failed", error);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal--hazmatDetail modal--open">
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(17, 25, 39, 0.5)",
          }}
        >
          <Loader />
        </div>
      )}

      <div className="modal__dialog">
        <div className="modal__content modal__content--lg">
          {/* Header */}
          <div className="modal__header">
            <h4 className="modal__header--title">
              {isEdit ? "Edit Order Status" : "Add New Order Status"}
            </h4>
            <button
              className="btn btn__transparent btnClose"
              onClick={handleClose}
              type="button"
            >
              <XCrossIcon />
            </button>
          </div>

          {/* Body */}
          <div className="modal__body modal-body-left">
            <div className="formRow formRow__2">
              {/* Onward Service Name */}
              <div className="formCol">
                <div className="form__Field">
                  <label
                    htmlFor="onwardServiceName"
                    className="form__label"
                  >
                    Order Status Name <span className="mandatory-mark">*</span>
                  </label>
                  <input
                    id="onwardServiceName"
                    type="text"
                    placeholder="Enter Name"
                    className="form__input form__input--sm form__input--disabled"
                    disabled={isEdit}
                    value={data.onwardServiceName}
                    onChange={(e) =>
                      setData((prev) => ({
                        ...prev,
                        onwardServiceName: e.target.value.trimStart(),
                      }))
                    }
                  />
                </div>
              </div>

              {/* Description */}
              <div className="formCol">
                <div className="form__Field">
                  <label htmlFor="description" className="form__label">
                    Onward Status Name
                  </label>
                  <input
                    id="description"
                    type="text"
                    placeholder="Enter Onward Status Name"
                    className="form__input form__input--sm"
                    value={data.description}
                    onChange={(e) =>
                      setData((prev) => ({
                        ...prev,
                        description: e.target.value.trimStart(),
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === " " && data.description.endsWith(",")) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="modal__footer">
            <div className="btn__group">
              <button
                className="btn btn__white"
                onClick={handleClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn btn__primary"
                onClick={handleSave}
                type="button"
                disabled={(isEdit && !isChanged)}
              >
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal__backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default AddEditOrderStatusModal;
